import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Eye, EyeOff, Phone, Lock, ArrowLeft, Store, User } from "lucide-react";
import { useFirebaseOTP } from "@/hooks/useFirebaseOTP";
import { apiError, normalizeMobile } from "@/lib/apiError";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_097dcc50-4812-4b75-a884-fb87f03fa7a9/artifacts/6cfgy4bw_logo%20%281%29.webp";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginType, setLoginType] = useState("user");
  const [formData, setFormData] = useState({
    mobile: "",
    password: "",
    otp: "",
  });

  const otp = useFirebaseOTP();
  const firebaseConfigured = !!process.env.REACT_APP_FIREBASE_API_KEY;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const sendOTP = async () => {
    await otp.sendOTP(formData.mobile);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (loginType === "user") {
        // If Firebase available AND user chose OTP flow → Firebase path
        if (firebaseConfigured && otp.otpSent) {
          const idToken = await otp.verifyOTP(formData.otp);
          if (!idToken) { setLoading(false); return; }
          const res = await axios.post(`${API}/auth/firebase`, {
            id_token: idToken,
            purpose: "login_user",
          });
          login(res.data.user, res.data.token);
        } else {
          const res = await axios.post(`${API}/auth/login/user`, {
            mobile: normalizeMobile(formData.mobile),
            password: formData.password,
          });
          login(res.data.user, res.data.token);
        }
        toast.success("Login successful!");
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from);
      } else {
        // Seller password login (unchanged)
        const res = await axios.post(`${API}/auth/login/seller`, {
          mobile: normalizeMobile(formData.mobile),
          password: formData.password,
        });
        const userData = res.data.seller;
        login(userData, res.data.token);
        toast.success("Login successful!");
        navigate(location.state?.from?.pathname || "/seller/dashboard");
      }
    } catch (error) {
      const status = error.response?.status;
      toast.error(
        status === 404
          ? "No account found with this mobile number. Please sign up first."
          : status === 401
          ? "Incorrect password. Try again or use Forgot Password."
          : apiError(error, "Login failed")
      );
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="M Ventures" className="h-12 mx-auto mb-4" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Welcome Back</h1>
          <p className="text-gray-500">Sign in to continue earning</p>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            <Tabs value={loginType} onValueChange={setLoginType} className="mb-6">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="user" className="gap-2" data-testid="login-tab-user">
                  <User className="w-4 h-4" />
                  <span>User</span>
                </TabsTrigger>
                <TabsTrigger value="seller" className="gap-2" data-testid="login-tab-seller">
                  <Store className="w-4 h-4" />
                  <span>Seller</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="mobile">Mobile Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="mobile"
                    name="mobile"
                    type="tel"
                    placeholder="Enter 10-digit mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className="pl-10"
                    maxLength={16}
                    required
                    data-testid="login-mobile-input"
                  />
                </div>
              </div>

              {loginType === "user" && firebaseConfigured && !otp.otpSent && (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={sendOTP}
                    disabled={otp.sending}
                    className="flex-1"
                    data-testid="send-otp-btn"
                  >
                    {otp.sending ? "Sending..." : "Login with OTP"}
                  </Button>
                </div>
              )}

              {firebaseConfigured && otp.otpSent && loginType === "user" ? (
                <div>
                  <Label htmlFor="otp">Enter OTP sent to +91{formData.mobile}</Label>
                  <Input
                    id="otp"
                    name="otp"
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={formData.otp}
                    onChange={handleChange}
                    maxLength={6}
                    required
                    data-testid="login-otp-input"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <button
                      type="button"
                      onClick={() => { otp.reset(); setFormData({ ...formData, otp: "" }); }}
                      className="text-sm text-primary-600"
                    >
                      Use password
                    </button>
                    <button
                      type="button"
                      onClick={sendOTP}
                      disabled={otp.sending}
                      className="text-sm text-primary-600 disabled:opacity-50"
                    >
                      Resend OTP
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400 rounded-full py-6"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            {/* Invisible reCAPTCHA container for Firebase */}
            <div id="recaptcha-container"></div>

            <p className="text-center mt-4 text-sm">
              <Link to="/forgot-password" className="text-primary-600 hover:underline">
                Forgot Password?
              </Link>
            </p>

            {loginType === "user" && (
              <p className="text-center mt-6 text-gray-500">
                Don't have an account?{" "}
                <Link to="/register" className="text-primary-600 font-medium hover:underline">
                  Sign up
                </Link>
              </p>
            )}

            {loginType === "seller" && (
              <p className="text-center mt-6 text-gray-500">
                Want to become a seller?{" "}
                <Link to="/seller/register" className="text-primary-600 font-medium hover:underline">
                  Register here
                </Link>
              </p>
            )}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
